import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * A comment box that offers people to @mention -- GitHub issue #75.
 *
 * TYPE `@` THEN A SURNAME. Matching runs last name -> first name -> email, with
 * last-name hits ranked first, because a surname is what somebody reaches for
 * first and a first-name substring would otherwise bury it ("@stec" must find
 * Steckelberg before it finds anyone whose given name contains those letters).
 * Email is matched too: it is the identifier people know exactly when a surname
 * is ambiguous or spelled unexpectedly.
 *
 * THE TEXT IS DISPLAY, THE IDS ARE WHAT IS SENT. Picking inserts the full name
 * ("@Jane Doe", never a bare surname -- two people share one often enough that
 * it reads ambiguously) and records the id separately. Nothing is ever parsed
 * back out of the prose to decide who gets notified, which is the same rule
 * that keeps a model's determined_document_type_name from resolving to a
 * document_type_id. If the author edits the name text afterwards the mention
 * still stands: the id is the fact.
 *
 * `people` is passed IN rather than fetched here -- the panel already knows the
 * document and fetches the list once, so a component per composer (there is one
 * per reply box) must not each fire their own request. Filtering is client-side
 * over that list, exactly as UserSelect does.
 */

const MAX_SUGGESTIONS = 8;

export function rankPeople(people, term) {
  const q = (term || '').trim().toLowerCase();
  if (!q) return people.slice(0, MAX_SUGGESTIONS);
  const scored = [];
  people.forEach((p) => {
    const last = (p.last_name || '').toLowerCase();
    const first = (p.first_name || '').toLowerCase();
    const email = (p.email || '').toLowerCase();
    // Lower rank sorts first. A prefix match on the surname is what somebody
    // typing "@stec" almost always means, so it outranks everything else.
    let rank = null;
    if (last.startsWith(q)) rank = 0;
    else if (first.startsWith(q)) rank = 1;
    else if (email.startsWith(q)) rank = 2;
    else if (last.includes(q)) rank = 3;
    else if (first.includes(q)) rank = 4;
    else if (email.includes(q)) rank = 5;
    if (rank !== null) scored.push({ p, rank });
  });
  scored.sort((a, b) => (
    a.rank - b.rank
    || (a.p.last_name || '').localeCompare(b.p.last_name || '')
    || (a.p.first_name || '').localeCompare(b.p.first_name || '')
  ));
  return scored.slice(0, MAX_SUGGESTIONS).map((s) => s.p);
}

/** The "@word" being typed immediately before the caret, or null. */
export function activeMentionQuery(value, caret) {
  const upto = (value || '').slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  // Only at a word boundary, so an email address typed into a comment does not
  // open the picker on its domain.
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const term = upto.slice(at + 1);
  if (/\s/.test(term)) return null;   // the mention is finished; stop offering
  return { at, term };
}

export const displayName = (p) => `${p.first_name} ${p.last_name}`.trim();

function MentionInput({
  value, onChange, people = [], onMention, placeholder, rows = 2, multiline = true,
  disabled = false,
}) {
  const [query, setQuery] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const suggestions = useMemo(
    () => (query ? rankPeople(people, query.term) : []),
    [query, people],
  );

  useEffect(() => { setHighlight(0); }, [query?.term]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setQuery(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    onChange(next);
    setQuery(activeMentionQuery(next, e.target.selectionStart));
  };

  const pick = (person) => {
    if (!query) return;
    const label = displayName(person);
    const before = value.slice(0, query.at);
    const after = value.slice(query.at + 1 + query.term.length);
    const next = `${before}@${label}${after.startsWith(' ') ? '' : ' '}${after}`;
    onChange(next);
    if (onMention) onMention(person);
    setQuery(null);
    // Put the caret after the inserted name rather than leaving it wherever the
    // browser decides once the value changes underneath it.
    const caret = before.length + label.length + 2;
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(caret, caret);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (!query || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Only swallow Enter while the picker is actually open with a match --
      // otherwise it would stop people writing a multi-line comment.
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setQuery(null);
    }
  };

  const common = {
    ref: inputRef,
    value,
    placeholder,
    disabled,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: () => { /* closed by the outside-mousedown handler, so a click on a
                       suggestion still lands */ },
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1 }}>
      {multiline
        ? <textarea rows={rows} {...common} />
        : <input style={{ width: '100%' }} {...common} />}
      {query && suggestions.length > 0 && (
        <div className="mention-suggestions">
          {suggestions.map((p, i) => (
            <button
              type="button"
              key={p.id}
              className={`mention-suggestion${i === highlight ? ' active' : ''}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(p)}
            >
              <span className="mention-suggestion-name">
                {p.last_name}, {p.first_name}
              </span>
              <span className="mention-suggestion-meta">
                {p.title ? `${p.title} — ` : ''}{p.email}
              </span>
            </button>
          ))}
        </div>
      )}
      {query && suggestions.length === 0 && query.term.length > 0 && (
        <div className="mention-suggestions">
          <div className="mention-suggestion-empty">
            Nobody matching "{query.term}" can read this policy.
          </div>
        </div>
      )}
    </div>
  );
}

export default MentionInput;
